import { createFileRoute, Link } from '@tanstack/react-router'
import type { ColDef } from 'ag-grid-community'
import { useMemo, useState } from 'react'

import { useLinkDeviceToPointOfSale, useUnlinkDeviceFromPointOfSale } from '@/api/api-mutations'
import { preloadGetDevice, preloadGetDeviceStatus, useGetDevice, useListPointsOfSale } from '@/api/react-query'
import { AccessKeyDevelopmentModeCell } from '@/components/access-key/create-access-key-drawer'
import { Badge } from '@/components/base/badge'
import { Button } from '@/components/base/button'
import { Container, ContainerHeader } from '@/components/base/container'
import { Copy } from '@/components/base/copy'
import { Drawer } from '@/components/base/drawer'
import { EmptyState } from '@/components/base/empty-state'
import { Heading } from '@/components/base/heading'
import { IconButton } from '@/components/base/icon-button'
import { usePrompt } from '@/components/base/prompt'
import { SectionRow } from '@/components/base/section-row'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { DeviceManagementPanel } from '@/components/devices/device-management-panel'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { SingleColumnPage } from '@/components/page-layout'
import { Table } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'
import { PencilSquare } from '@/icons/pencil-square'
import { ConditionalRouteComponent } from '@/utils/route-utilities'

export const Route = createFileRoute('/_authenticated/settings/devices/$deviceUuid')({
	component: () => (
		<ConditionalRouteComponent routePath={Route.id} pathsToRenderOn={['/edit']}>
			<DeviceDetail />
		</ConditionalRouteComponent>
	),
	loader: async ({ context, params: { deviceUuid } }) => {
		const [device] = await Promise.all([
			preloadGetDevice(context, { path: { deviceUuid } }),
			preloadGetDeviceStatus(context, { path: { deviceUuid } })
		])
		return device
	},
	staticData: {
		crumb: ({ loaderData }) => loaderData?.name || 'Device'
	}
})

function LinkPointOfSaleDrawer({
	deviceUuid,
	open,
	onOpenChange
}: {
	deviceUuid: string
	open: boolean
	onOpenChange: (open: boolean) => void
}) {
	const { L } = useI18n()
	const pointsOfSale = useListPointsOfSale()
	const linkDevice = useLinkDeviceToPointOfSale()

	const pointOfSaleOptions = useMemo(
		() => (pointsOfSale.data ?? []).map(pos => ({ value: pos.uuid, label: pos.name })),
		[pointsOfSale.data]
	)

	const form = useAppForm({
		defaultValues: { pointOfSaleUuid: '' },
		onSubmit: async ({ value }) => {
			if (!value.pointOfSaleUuid) {
				return
			}
			const result = await linkDevice({
				path: { deviceUuid },
				body: { pointOfSaleUuid: value.pointOfSaleUuid }
			})
			if (result.isSuccess) {
				toast.success(L.Settings.Devices.LinkSuccess())
				onOpenChange(false)
				form.reset()
			} else {
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<Drawer open={open} onOpenChange={onOpenChange}>
			<Drawer.Content>
				<form.AppForm>
					<Drawer.Header>
						<Drawer.Title asChild>
							<Heading>{L.Settings.Devices.LinkTitle()}</Heading>
						</Drawer.Title>
						<Drawer.Description>{L.Settings.Devices.LinkDescription()}</Drawer.Description>
					</Drawer.Header>
					<Drawer.Body>
						<FormContainer>
							<FormFieldGroup>
								<form.AppField
									name='pointOfSaleUuid'
									validators={{
										onChange: ({ value }) => (!value ? L.General.Required() : undefined)
									}}
									children={field => (
										<field.SelectField
											label={L.Settings.Devices.SelectPointOfSale()}
											options={pointOfSaleOptions}
											optionsLoading={pointsOfSale.isLoading}
										/>
									)}
								/>
							</FormFieldGroup>
						</FormContainer>
					</Drawer.Body>
					<Drawer.Footer>
						<Button variant='secondary' size='small' onClick={() => onOpenChange(false)}>
							{L.General.Cancel()}
						</Button>
						<form.SaveButton label={L.Settings.Devices.LinkButton()} />
					</Drawer.Footer>
				</form.AppForm>
			</Drawer.Content>
		</Drawer>
	)
}

function DeviceDetail() {
	const { L } = useI18n()
	const { deviceUuid } = Route.useParams()
	const prompt = usePrompt()
	const [linkOpen, setLinkOpen] = useState(false)

	const device = useGetDevice({ deviceUuid })
	const unlinkDevice = useUnlinkDeviceFromPointOfSale(true)

	const accessKeyColumnDefs = useMemo<ColDef[]>(
		() => [
			{
				headerName: L.Fields.Name(),
				field: 'name',
				flex: 2
			},
			{
				headerName: 'Type',
				field: 'type',
				flex: 1
			},
			{
				headerName: 'Token',
				field: 'token',
				flex: 3,
				cellRenderer: ({ value, data }: { value?: string; data: any }) => {
					const displayValue = data.type === 'PUBLISHABLE' ? value : data.redacted
					const isPublishable = data.type === 'PUBLISHABLE'
					return (
						<div className='flex h-full items-center gap-2'>
							<code className='bg-muted rounded px-2 py-1 text-xs'>
								{displayValue ? `${displayValue.substring(0, 20)}...` : null}
							</code>
							{isPublishable && value && <Copy content={value} variant='mini' />}
						</div>
					)
				}
			},
			{
				headerName: L.Fields.CreatedAt(),
				field: 'createdAt',
				type: 'date',
				flex: 1
			},
			{
				headerName: L.Settings.AccessKeys.Field.DevelopmentMode(),
				flex: 1,
				cellRenderer: AccessKeyDevelopmentModeCell
			}
		],
		[L]
	)

	if (!device.data) {
		return null
	}

	const linkedPointOfSale = device.data.pointOfSale

	const handleUnlink = async () => {
		const confirmed = await prompt({
			variant: 'danger',
			title: L.Settings.Devices.UnlinkConfirmTitle(),
			description: L.Settings.Devices.UnlinkConfirmDescription()
		})
		if (!confirmed) {
			return
		}
		const result = await unlinkDevice.mutateAsync({ path: { deviceUuid } })
		if (!result.isSuccess) {
			return
		}
		toast.success(L.Settings.Devices.UnlinkSuccess())
	}

	return (
		<SingleColumnPage hasOutlet>
			<Container>
				<ContainerHeader
					heading={device.data.name}
					rightSide={
						<Link from={Route.fullPath} to='./edit'>
							<IconButton tooltipText={L.General.Edit()}>
								<PencilSquare />
							</IconButton>
						</Link>
					}
				/>
				<SectionRow title={L.Settings.Devices.Identifier()} value={device.data.identifier} />
				<SectionRow
					title={L.Settings.Devices.OperatingModes()}
					value={
						device.data.allowedOperatingModes.length > 0 ? (
							<div className='flex flex-wrap gap-1'>
								{device.data.allowedOperatingModes.map(mode => (
									<Badge key={mode} size='2xsmall'>
										{L.Settings.PointOfSale.OperatingMode(mode)}
									</Badge>
								))}
							</div>
						) : (
							<Text size='small' className='text-ui-fg-muted'>
								{L.General.None()}
							</Text>
						)
					}
				/>
			</Container>

			<Container>
				<ContainerHeader
					heading={L.Settings.Devices.LinkedPointOfSale()}
					rightSide={
						linkedPointOfSale ? (
							<Button
								size='small'
								variant='secondary'
								isLoading={unlinkDevice.isPending}
								onClick={handleUnlink}
							>
								{L.Settings.Devices.UnlinkButton()}
							</Button>
						) : (
							<Button size='small' variant='secondary' onClick={() => setLinkOpen(true)}>
								{L.Settings.Devices.LinkButton()}
							</Button>
						)
					}
				/>
				<SectionRow
					title={L.Settings.Devices.LinkedPointOfSale()}
					value={
						linkedPointOfSale ? (
							<Link
								to='/settings/points-of-sale/$pointOfSaleUuid'
								params={{ pointOfSaleUuid: linkedPointOfSale.uuid }}
							>
								<Badge size='2xsmall'>{linkedPointOfSale.name}</Badge>
							</Link>
						) : (
							L.Settings.Devices.NotLinked()
						)
					}
				/>
			</Container>

			<Container>
				<ContainerHeader heading={L.Settings.Devices.MarketingChannel()} />
				<SectionRow
					title={L.Settings.Devices.MarketingChannel()}
					value={
						device.data.marketingChannel ? (
							<Badge size='2xsmall'>{device.data.marketingChannel.name}</Badge>
						) : (
							L.Settings.Devices.NoMarketingChannel()
						)
					}
				/>
			</Container>

			<Container>
				<ContainerHeader heading={L.Settings.PointOfSale.Column.AccessKeys()} />
				<Table
					className='w-full'
					data={device.data.accessKeys}
					columnUtilities={{}}
					columnDefs={accessKeyColumnDefs}
					animateRows
					domLayout='autoHeight'
					rowSelection={{
						mode: 'singleRow',
						checkboxes: false,
						enableClickSelection: false,
						enableSelectionWithoutKeys: false
					}}
					suppressCellFocus
					emptyState={
						<EmptyState
							title={L.Settings.Devices.AccessKeysEmptyTitle()}
							description={L.Settings.Devices.AccessKeysEmptyDescription()}
							className='min-h-[200px]'
						/>
					}
				/>
			</Container>

			<DeviceManagementPanel
				deviceUuid={deviceUuid}
				deviceName={device.data.name}
				linkedPointOfSaleUuid={linkedPointOfSale?.uuid}
			/>

			<LinkPointOfSaleDrawer deviceUuid={deviceUuid} open={linkOpen} onOpenChange={setLinkOpen} />
		</SingleColumnPage>
	)
}
